/**
 * What some widgets need from their surroundings without knowing about the
 * world: the entities an `entityRef` can point at, the assets an `assetRef`
 * can pick, the clips a `clipRef` can play, and the theme's color tokens a
 * `color` can bind to. The Inspector provides them.
 */
import { createContext, useContext } from "react";
import type { ClipSource, EnumOption } from "../../world/schema/propTypes";

/** An asset to offer: the path an `assetRef` stores, a short label, and where it lives. */
export interface AssetOption {
  readonly value: string;
  readonly label: string;
  /** Secondary text, e.g. its folder and size. */
  readonly detail: string;
  /** A preview image's URL (transparent background), when the asset has one. */
  readonly thumbnail?: string;
}

export interface WidgetEnvironment {
  /** Entities to offer, optionally only those with a component type. */
  entityOptions(requires?: string): readonly EnumOption[];
  /** Assets of some kinds (`gltf`, `texture`, the engine's `shape`), built-in ones first. */
  assetOptions(kinds: readonly string[]): readonly AssetOption[];
  /** Clip names of the model a `clipRef`'s source prop points at, on the edited entity. */
  clipOptions(source: ClipSource): readonly string[];
  /** `viewport.*` color tokens a color prop can follow. */
  readonly colorTokens: readonly string[];
}

const EMPTY: WidgetEnvironment = {
  entityOptions: () => [],
  assetOptions: () => [],
  clipOptions: () => [],
  colorTokens: [],
};

const Context = createContext<WidgetEnvironment>(EMPTY);

export const WidgetEnvironmentProvider = Context.Provider;

export function useWidgetEnvironment(): WidgetEnvironment {
  return useContext(Context);
}
